import { useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { toast } from "sonner";
import { Mail } from "lucide-react";

const LOGO_URL = "/cgi-logo.png";
const CGI_WEBSITE = "https://www.commongroundinstitute.net";

export default function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState("");

  const onSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError("");
    try {
      await api.post("/auth/forgot-password", { email: email.trim() });
      setSent(true);
    } catch (err) {
      const msg = formatApiErrorDetail(err.response?.data?.detail) || err.message;
      setError(msg);
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-6 sm:p-10 bg-clinical" data-testid="forgot-password-page">
      <div className="w-full max-w-md animate-fade-in-up">
        <div className="flex flex-col items-start gap-3 mb-10">
          <a href={CGI_WEBSITE} target="_blank" rel="noopener noreferrer" aria-label="Visit the Common Ground Institute website">
            <img src={LOGO_URL} alt="Common Ground Institute" className="h-20 w-auto object-contain rounded-md" />
          </a>
          <h1 className="font-heading text-3xl sm:text-4xl font-semibold text-slate-900 mt-4">
            Forgot your password?
          </h1>
          <p className="text-slate-600 leading-relaxed">
            Enter the email you signed up with. If we find an account, we&apos;ll send a link to reset your password.
          </p>
        </div>

        <Card className="border-slate-200 shadow-sm">
          <CardContent className="p-6 sm:p-8">
            {sent ? (
              <div className="text-center" data-testid="forgot-success">
                <div className="h-12 w-12 rounded-full bg-accent text-primary flex items-center justify-center mx-auto mb-4">
                  <Mail className="h-6 w-6" />
                </div>
                <h2 className="font-heading text-xl font-semibold text-slate-900">Check your inbox</h2>
                <p className="mt-2 text-slate-600 text-sm leading-relaxed">
                  If an account exists for <strong>{email}</strong>, a reset link is on its way. The link expires in 60 minutes.
                </p>
                <Button asChild variant="outline" className="mt-6">
                  <Link to="/login" data-testid="forgot-back-to-login">Back to sign in</Link>
                </Button>
              </div>
            ) : (
              <form onSubmit={onSubmit} className="space-y-5" data-testid="forgot-form">
                <div className="space-y-2">
                  <Label htmlFor="email">Email</Label>
                  <Input
                    id="email" type="email" autoComplete="email" required value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    data-testid="forgot-email-input"
                  />
                </div>
                {error && (
                  <div className="text-sm text-red-600 bg-red-50 border border-red-100 rounded-md px-3 py-2" data-testid="forgot-error">
                    {error}
                  </div>
                )}
                <Button
                  type="submit"
                  disabled={submitting}
                  className="w-full bg-primary hover:bg-primary/90 text-primary-foreground"
                  data-testid="forgot-submit-button"
                >
                  {submitting ? "Sending…" : "Send reset link"}
                </Button>
                <p className="text-sm text-slate-600 text-center">
                  Remembered it?{" "}
                  <Link to="/login" className="text-primary font-medium hover:underline" data-testid="forgot-to-login-link">
                    Sign in
                  </Link>
                </p>
              </form>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
